import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CircleNotch } from 'phosphor-react';
import { RootState, AppDispatch } from '@/store';
import { markWaiverSigned } from '@/store/waiverSlice';
import waiverService, { WaiverData } from '@/services/waiverService';
import mainLogo from '../../../assets/main_logo.png';
import styles from './Waiver.module.css';

interface WaiverSigningPageProps {
  waiver?: WaiverData;
  onSigned?: () => void;
}

const WaiverSigningPage = ({
  waiver: propWaiver,
  onSigned,
}: WaiverSigningPageProps = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { activeWaiver: reduxWaiver, loading: statusLoading } = useSelector(
    (state: RootState) => state.waiver
  );

  // When propWaiver is provided (inline booking mode), use it directly
  const activeWaiver = propWaiver ?? reduxWaiver;

  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAgree = async () => {
    if (!activeWaiver || signing) return;
    setSigning(true);
    setError(null);
    try {
      await waiverService.signWaiver(activeWaiver.id);
      dispatch(markWaiverSigned());
      if (onSigned) {
        onSigned();
      } else {
        // D-04: AuthGuard re-evaluates, navigates to original destination or home
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Something went wrong. Please try again.'
      );
      setSigning(false);
    }
  };

  // Skeleton loading state while waiver status is being fetched (skip if waiver passed as prop)
  if (statusLoading && !propWaiver) {
    return (
      <div className={styles.waiverPage}>
        <img src={mainLogo} alt="Tee Box" className={styles.logo} />
        <h1 className={styles.pageTitle}>Review &amp; Sign Waiver</h1>
        <p className={styles.pageSubtitle}>
          Please read the waiver below and tap I Agree to continue.
        </p>
        <div className={styles.waiverCard}>
          <div className={styles.scrollArea}>
            <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
            <div className={`${styles.skeleton} ${styles.skeletonMeta}`} />
            <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
            <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
            <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
            <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
          </div>
        </div>
      </div>
    );
  }

  // No active waiver — should not normally reach here (AuthGuard would not redirect)
  if (!activeWaiver) {
    return (
      <div className={styles.waiverPage}>
        <img src={mainLogo} alt="Tee Box" className={styles.logo} />
        <div className={styles.errorState}>
          <p>No waiver is currently required. Continue to the app.</p>
        </div>
      </div>
    );
  }

  const formattedDate = activeWaiver.publishedAt
    ? new Date(activeWaiver.publishedAt).toLocaleDateString('en-US', {
        dateStyle: 'long',
      })
    : '';

  return (
    <div className={styles.waiverPage}>
      <img src={mainLogo} alt="Tee Box" className={styles.logo} />
      <h1 className={styles.pageTitle}>Review &amp; Sign Waiver</h1>
      <p className={styles.pageSubtitle}>
        Please read the waiver below and tap I Agree to continue.
      </p>
      <div className={styles.waiverCard}>
        <div className={styles.scrollArea}>
          <h2 className={styles.waiverTitle}>{activeWaiver.title}</h2>
          <p className={styles.waiverMeta}>
            Version {activeWaiver.version}
            {formattedDate ? ` · Effective ${formattedDate}` : ''}
          </p>
          {/* WVSN-02: Full waiver text displayed via activeWaiver.body from WaiverResponseDto */}
          <div
            className={styles.waiverContent}
            dangerouslySetInnerHTML={{ __html: activeWaiver.body }}
          />
        </div>
        <div className={styles.ctaStrip}>
          {error && (
            <p
              style={{
                color: 'var(--tbx-error, #dc2626)',
                fontSize: '13px',
                marginBottom: '8px',
                textAlign: 'center',
              }}
            >
              {error}
            </p>
          )}
          <button
            className={styles.agreeButton}
            onClick={handleAgree}
            disabled={signing}
          >
            {signing ? (
              <CircleNotch size={20} className={styles.spinner} />
            ) : (
              'I Agree'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaiverSigningPage;
